import api from "../api/axios";

/**
 * Converts a Base64URL string to Uint8Array for applicationServerKey.
 */
export function urlBase64ToUint8Array(base64String) {
  const cleanBase64 = String(base64String).trim().replace(/^["']|["']$/g, "");
  const padding = "=".repeat((4 - (cleanBase64.length % 4)) % 4);
  const base64 = (cleanBase64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Checks if Web Push is supported by the current browser.
 */
export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/**
 * Returns current notification permission state: 'granted' | 'denied' | 'default' | 'unsupported'.
 */
export function getNotificationPermission() {
  if (!isPushSupported()) return "unsupported";
  return Notification.permission;
}

/**
 * Registers the CRM service worker script.
 */
export async function registerServiceWorker() {
  if (!isPushSupported()) return null;
  try {
    const registration = await navigator.serviceWorker.register("/service-worker.js");
    return registration;
  } catch (error) {
    console.error("Service worker registration failed:", error);
    return null;
  }
}

/**
 * Fetches VAPID public key from backend API.
 */
export async function getPublicVapidKey() {
  try {
    const res = await api.get("/notifications/push/public-key");
    return res.data?.public_key || null;
  } catch (err) {
    console.error("Failed to fetch VAPID public key:", err);
    return null;
  }
}

/**
 * Subscribes the current device/browser to Web Push notifications.
 * Handles key updates and browser push service key mismatches gracefully.
 */
export async function subscribeUserToPush() {
  if (!isPushSupported()) {
    throw new Error("Web Push is not supported in this browser.");
  }

  // Request permission if not already granted
  let permission = Notification.permission;
  if (permission === "default") {
    permission = await Notification.requestPermission();
  }

  if (permission !== "granted") {
    throw new Error("Desktop notification permission was denied. Please enable notifications in your browser settings.");
  }

  const registration = await registerServiceWorker();
  if (!registration) {
    throw new Error("Could not register service worker.");
  }

  let vapidPublicKey = await getPublicVapidKey();
  if (!vapidPublicKey) {
    throw new Error("Could not retrieve VAPID public key from server.");
  }

  vapidPublicKey = String(vapidPublicKey).trim().replace(/^["']|["']$/g, "");
  const convertedKey = urlBase64ToUint8Array(vapidPublicKey);

  let subscription = null;

  try {
    const existingSub = await registration.pushManager.getSubscription();
    if (existingSub) {
      // Unsubscribe stale subscription to avoid VAPID key mismatch push service errors
      await existingSub.unsubscribe().catch(() => {});
    }

    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: convertedKey,
    });
  } catch (err) {
    console.warn("First push subscription attempt failed, retrying with clean state...", err);
    try {
      const existingSub = await registration.pushManager.getSubscription();
      if (existingSub) {
        await existingSub.unsubscribe().catch(() => {});
      }
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: convertedKey,
      });
    } catch (retryErr) {
      console.error("Push subscription retry error:", retryErr);
      if (retryErr.message?.includes("push service error")) {
        throw new Error(
          "Browser push service error. Please reset notification permission or clear site data for localhost in browser settings."
        );
      }
      throw retryErr;
    }
  }

  const subJson = subscription.toJSON();

  // Send subscription credentials to backend (authenticated user derived from JWT)
  await api.post("/notifications/push/subscribe", {
    endpoint: subJson.endpoint,
    keys: {
      p256dh: subJson.keys?.p256dh || "",
      auth: subJson.keys?.auth || "",
    },
    user_agent: navigator.userAgent,
  });

  return subscription;
}

/**
 * Unsubscribes the current device/browser from Web Push notifications.
 */
export async function unsubscribeUserFromPush() {
  if (!isPushSupported()) return false;

  try {
    const registration = await navigator.serviceWorker.getRegistration("/service-worker.js");
    if (!registration) return false;

    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      const endpoint = subscription.endpoint;
      await api.delete("/notifications/push/subscribe", {
        data: { endpoint },
      }).catch(() => {});
      await subscription.unsubscribe().catch(() => {});
      return true;
    }
  } catch (err) {
    console.error("Error unsubscribing from push:", err);
  }
  return false;
}

/**
 * Returns current Web Push subscription status details.
 * Non-blocking: Uses getRegistration and timeout safeguard instead of indefinite ready Promise.
 */
export async function getSubscriptionStatus() {
  if (!isPushSupported()) {
    return { supported: false, permission: "unsupported", isSubscribed: false };
  }

  const permission = Notification.permission;
  if (permission !== "granted") {
    return { supported: true, permission, isSubscribed: false };
  }

  try {
    const registration = await Promise.race([
      navigator.serviceWorker.getRegistration("/service-worker.js"),
      new Promise((resolve) => setTimeout(() => resolve(null), 2000)),
    ]);

    if (!registration) {
      return { supported: true, permission, isSubscribed: false };
    }

    const subscription = await Promise.race([
      registration.pushManager.getSubscription(),
      new Promise((resolve) => setTimeout(() => resolve(null), 2000)),
    ]);

    return {
      supported: true,
      permission,
      isSubscribed: Boolean(subscription),
    };
  } catch (err) {
    return { supported: true, permission, isSubscribed: false };
  }
}
